/**
 * What this browser can do with passkeys, and what to tell a guest when a ceremony fails.
 *
 * Every check is guarded: a browser without WebAuthn has no PublicKeyCredential at all, and reading a static method
 * off undefined is a ReferenceError, not a false.
 *
 * @changelog
 *
 * 2026-10-10 (0.1.0) (jhoward)
 *   - Add isWebAuthnSupported(), hasPlatformAuthenticator(), isConditionalMediationAvailable() and
 *     describeWebAuthnError().
 *
 * 2026-10-23 (0.2.0) (jhoward)
 *   - Add canUsePasskeysHere(). Every property domain now uses one relying party ID, which a page on any other domain
 *     may only use through WebAuthn Related Origin Requests - and not every browser supports those.
 *   - describeWebAuthnError() points a SecurityError on one of those domains at the other sign-in methods.
 */

export type WebAuthnCeremony = 'create' | 'get';

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'PublicKeyCredential' in window &&
    typeof navigator?.credentials?.create === 'function' &&
    typeof navigator?.credentials?.get === 'function'
  );
}

//The relying party ID every property domain shares, as the server wrote it in resources/views/app.blade.php.
function getRelyingPartyId(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="webauthn-rp-id"]')?.content.trim().toLowerCase() ?? '';
}

//Whether this page's own domain is the relying party ID, or a subdomain of it: no related origin needed.
function isRelyingPartyDomain(): boolean {
  const rpId = getRelyingPartyId();
  const host = window.location.hostname.toLowerCase();

  return '' !== rpId && (host === rpId || host.endsWith('.' + rpId));
}

let passkeysHere: Promise<boolean> | null = null;

/**
 * Whether a passkey ceremony can run on this domain. On the relying party's own domain that is only WebAuthn
 * support. On the other property domains the browser must also support Related Origin Requests, which it reports
 * through getClientCapabilities(); a browser without that method is taken not to (Firefox, older Chrome and Safari).
 * Answered once per page load.
 */
export function canUsePasskeysHere(): Promise<boolean> {
  passkeysHere ??= (async (): Promise<boolean> => {
    if (!isWebAuthnSupported()) {
      return false;
    }
    if (isRelyingPartyDomain()) {
      return true;
    }

    const getClientCapabilities = (
      PublicKeyCredential as unknown as { getClientCapabilities?: () => Promise<Record<string, boolean>> }
    ).getClientCapabilities;
    if (typeof getClientCapabilities !== 'function') {
      return false;
    }
    try {
      return true === (await getClientCapabilities.call(PublicKeyCredential)).relatedOrigins;
    } catch {
      return false;
    }
  })();

  return passkeysHere;
}

/**
 * Whether the device itself can be the authenticator (Face ID, Touch ID, Windows Hello, Android biometrics).
 */
export async function hasPlatformAuthenticator(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false;
  }
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Whether the browser can offer passkeys from the username field's autofill.
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported() || typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') {
    return false;
  }
  try {
    return await PublicKeyCredential.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}

/**
 * A sentence for the guest, from the DOMException a ceremony threw. The browser's own messages are written for
 * developers ("The operation either timed out or was not allowed").
 */
export function describeWebAuthnError(error: unknown, ceremony: WebAuthnCeremony): string {
  const name = error instanceof Error ? error.name : '';

  switch (name) {
    case 'NotAllowedError':
      return 'The request was cancelled or timed out. Try again.';
    case 'InvalidStateError':
      return ceremony === 'create'
        ? 'This device is already registered as a passkey on this account.'
        : "This passkey isn't registered on this account.";
    case 'NotSupportedError':
      return "Your browser doesn't support passkeys.";
    case 'SecurityError':
      return isRelyingPartyDomain()
        ? "Passkeys can't be used from this address. Open the site from its usual address and try again."
        : "This browser can't use your passkey on this site. Choose another way to sign in, or try a current version of Chrome, Edge or Safari.";
    case 'ConstraintError':
      return "This device can't store a passkey here. Try a different device or security key.";
    case 'AbortError':
      return 'The request was cancelled.';
    default:
      return ceremony === 'create' ? "We couldn't add that passkey. Try again." : "We couldn't verify your passkey. Try again.";
  }
}
